import { useState, useMemo } from "react";
import { SectionHeader } from "./index";
import { toSatoshi } from "../../sdk";
import { Bucket } from "../../sdk/types/data";
import { TradeResult } from "../../hooks/useTradeOperation";
import OrderPlacementTab from "./OrderPlacementTab";
import CancelOpensTab from "./CancelOpensTab";
import PositionCloseTab from "./PositionCloseTab";

export interface OrderOperationSectionProps {
  // Status
  isReady: boolean;
  loading: boolean;
  result: TradeResult | null;

  // Market data
  selectedMarketId: string | number | null;
  buckets: Bucket[];
  markPrice?: { mark_price: number } | null;
  lastPrice?: { price: number } | null;

  // Order placement state
  orderPlacementMnemonic: string;
  setOrderPlacementMnemonic: (value: string) => void;
  orderPlacementBucketId: string;
  setOrderPlacementBucketId: (value: string) => void;
  orderPlacementSide: "buy" | "sell";
  setOrderPlacementSide: (value: "buy" | "sell") => void;
  orderPlacementOrderType: "market" | "limit";
  setOrderPlacementOrderType: (value: "market" | "limit") => void;
  orderPlacementPrice: number;
  setOrderPlacementPrice: (value: number) => void;
  orderPlacementQuantity: number;
  setOrderPlacementQuantity: (value: number) => void;
  orderPlacementLeverage: number;
  setOrderPlacementLeverage: (value: number) => void;

  // Handlers
  handlePlaceOrder: () => Promise<void>;
  handleRandomize: () => void;
  getOrderPrice: () => number;
  calculateSize: () => number;
}

type TabType = "order-placement" | "cancel-opens" | "position-close";

export default function OrderOperationSection({
  isReady,
  loading,
  result,
  selectedMarketId,
  buckets,
  markPrice,
  lastPrice,
  orderPlacementMnemonic,
  setOrderPlacementMnemonic,
  orderPlacementBucketId,
  setOrderPlacementBucketId,
  orderPlacementSide,
  setOrderPlacementSide,
  orderPlacementOrderType,
  setOrderPlacementOrderType,
  orderPlacementPrice,
  setOrderPlacementPrice,
  orderPlacementQuantity,
  setOrderPlacementQuantity,
  orderPlacementLeverage,
  setOrderPlacementLeverage,
  handlePlaceOrder,
  handleRandomize,
  getOrderPrice,
  calculateSize,
}: OrderOperationSectionProps) {
  const [activeTab, setActiveTab] = useState<TabType>("order-placement");

  const orderPlacementMarketId = selectedMarketId ? String(selectedMarketId) : "0";

  // Calculate order config JSON for display
  const orderConfigJSON = useMemo(() => {
    const orderPrice = getOrderPrice();
    const size = calculateSize();
    const leverageStr = toSatoshi(orderPlacementLeverage.toString());

    return {
      marketId: orderPlacementMarketId,
      bucketId: orderPlacementBucketId || "(required)",
      side: orderPlacementSide,
      orderType: orderPlacementOrderType,
      price: orderPlacementPrice,
      quantity: size > 0 ? size : orderPlacementQuantity || 0,
      leverage: leverageStr,
    };
  }, [
    orderPlacementMarketId,
    orderPlacementBucketId,
    orderPlacementSide,
    orderPlacementOrderType,
    orderPlacementPrice,
    orderPlacementQuantity,
    orderPlacementLeverage,
    getOrderPrice,
    calculateSize,
  ]);

  return (
    <div className="App-section">
      <SectionHeader
        title="Order Operations"
        statusBadges={[
          <span
            key="ready-status"
            className={`hooks-test-status-badge ${
              isReady
                ? "hooks-test-status-badge-success"
                : "hooks-test-status-badge-warning"
            }`}
          >
            {isReady ? "Ready" : "Initializing..."}
          </span>,
        ]}
      />

      <div
        style={{
          display: "flex",
          gap: "20px",
          alignItems: "start",
        }}
      >
        {/* Left Column: Tabs and Content */}
        <div style={{ flex: 1 }}>
          {/* Tabs */}
          <div className="hooks-test-multifeed-tabs">
            <button
              className={`hooks-test-multifeed-tab ${
                activeTab === "order-placement" ? "active" : ""
              }`}
              onClick={() => setActiveTab("order-placement")}
            >
              Order Placement
            </button>
            <button
              className={`hooks-test-multifeed-tab ${
                activeTab === "cancel-opens" ? "active" : ""
              }`}
              onClick={() => setActiveTab("cancel-opens")}
            >
              Cancel Opens
            </button>
            <button
              className={`hooks-test-multifeed-tab ${
                activeTab === "position-close" ? "active" : ""
              }`}
              onClick={() => setActiveTab("position-close")}
            >
              Position Close
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "order-placement" && (
            <OrderPlacementTab
              buckets={buckets}
              orderPlacementMnemonic={orderPlacementMnemonic}
              setOrderPlacementMnemonic={setOrderPlacementMnemonic}
              orderPlacementBucketId={orderPlacementBucketId}
              setOrderPlacementBucketId={setOrderPlacementBucketId}
              orderPlacementSide={orderPlacementSide}
              setOrderPlacementSide={setOrderPlacementSide}
              orderPlacementOrderType={orderPlacementOrderType}
              setOrderPlacementOrderType={setOrderPlacementOrderType}
              orderPlacementPrice={orderPlacementPrice}
              setOrderPlacementPrice={setOrderPlacementPrice}
              orderPlacementQuantity={orderPlacementQuantity}
              setOrderPlacementQuantity={setOrderPlacementQuantity}
              orderPlacementLeverage={orderPlacementLeverage}
              setOrderPlacementLeverage={setOrderPlacementLeverage}
              isReady={isReady}
              loading={loading}
              result={result}
              handlePlaceOrder={handlePlaceOrder}
              handleRandomize={handleRandomize}
            />
          )}

          {activeTab === "cancel-opens" && (
            <CancelOpensTab
              buckets={buckets}
              selectedMarketId={selectedMarketId}
            />
          )}

          {activeTab === "position-close" && (
            <PositionCloseTab
              buckets={buckets}
              selectedMarketId={selectedMarketId}
            />
          )}
        </div>

        {/* Right Column: JSON Config Display */}
        <div style={{ flex: 1 }}>
          <h3 className="hooks-test-section-header" style={{ marginTop: 0 }}>
            Config JSON
          </h3>
          <div className="hooks-test-result-data">
            <pre className="data-modal-content">
              {JSON.stringify(orderConfigJSON, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
